import React, { useState, useEffect } from 'react'
import { Classes } from '@blueprintjs/core'
import { Dialog } from '@ui'
import { IconButton, Confirmation, Toaster, Button } from '@ui'
import { NavigateFn } from '@reach/router'
import ImportProgress from './ImportProgress'
import SelectFileStep from './SelectFileStep'
import useActiveImport from './hooks/useActiveImport'
import useFileReader from './hooks/useFileReader'
import ImportSummary from './ImportSummary'
import api from '@api'
import { useReload } from '@hooks/reloadContext'
// import track from '@track'
// import Event from '@track/Event'

enum View {
  FileSelect,
  Progress,
  Summary,
}

interface BookingImportDialog {
  navigate: NavigateFn
  reload?: () => void
}

const BookingImportFlow: React.FunctionComponent<BookingImportDialog> = ({
  navigate,
  reload,
}: BookingImportDialog) => {
  const [dirty, setDirty] = useState(false)
  const { activeImport } = useActiveImport()
  const [importId, setImportId] = useState('')
  const [view, setView] = useState<View>(View.FileSelect)
  const { file, setFile, fileContent } = useFileReader()
  const [importing, setImporting] = useState(false)
  const reloader = useReload()

  useEffect(() => {
    if (activeImport) {
      setImportId(activeImport.id)
      setView(View.Progress)
    }
  }, [activeImport])

  function onClose() {
    navigate && navigate('?newItem')
  }

  return (
    <Dialog
      isOpen={true}
      style={{ position: 'relative', minWidth: 500 }}
      canEscapeKeyClose={false}
      canOutsideClickClose={false}
      transitionDuration={200}
      isCloseButtonShown={false}
      title={view === View.FileSelect ? 'Import Bookings' : ''}
      toolbar={
        <div className="dialog-buttons">
          <IconButton minimal round tooltip="Close" iconName="times" onClick={onClose} />
        </div>
      }
      body={
        <div className={Classes.DIALOG_BODY}>
          {view === View.FileSelect ? (
            <SelectFileStep
              file={file}
              onChange={(file) => {
                setFile(file)
                setDirty(true)
              }}
            />
          ) : view === View.Progress ? (
            <ImportProgress
              {...{
                file,
                importId,
                callback: (error) => {
                  if (error) {
                    setView(View.FileSelect)
                    Toaster.fail({ error })
                    return
                  }
                  reload?.()
                  reloader.bookings()
                  setView(View.Summary)
                },
              }}
            />
          ) : view === View.Summary ? (
            <ImportSummary id={importId} onClose={onClose} />
          ) : null}
        </div>
      }
      footer={
        <div className={`${Classes.DIALOG_FOOTER} flex justify-end mt-1`}>
          {view === View.FileSelect && (
            <Button
              disabled={!file}
              text="Import"
              loading={importing}
              onClick={() => {
                setImporting(true)
                setView(View.Progress)
                api.import.upload({ file: fileContent }, 'booking').then((data) => {
                  setImportId(data.id)
                  setDirty(false)
                })
              }}
            />
          )}
        </div>
      }
    />
  )
}

export default BookingImportFlow
